import {
    type CreateExtArgs,
    type CreateExtension,
    type DeepFunctionMap,
    type ExtensionAPI,
    PAGE_FRAGMENT_TYPE
} from '@wix/document-manager-core'
import {runSiteMigratorsFactory} from './runSiteMigrators'
import type {PageAPI, PageExtensionAPI} from '../page'
import {runPageMigrators, runSantaDataFixers} from './fixPage'
import type {FixerVersioningConfig, Pointer, SantaDataFixerPlugin} from '@wix/document-services-types'
import type {AnyPageMigrator, SiteMigrator} from './dataMigration/dataMigrationRunner'
import type {SnapshotExtApi} from '../snapshots'
import {SNAPSHOTS} from '../../constants/constants'

const pushMasterPageToEnd = (pageIds: string[]) => {
    const index = pageIds.indexOf('masterPage')
    if (index !== -1) {
        pageIds.splice(index, 1)
    }
    pageIds.push('masterPage')
}

const filterLoadedPages = (pageIds: string[], pageAPI: PageAPI) =>
    pageIds.filter((pageId: string) => pageAPI.hasPageBeenLoaded(pageId))

export interface SantaDataFixersSettings {
    santaDataFixerPlugins: SantaDataFixerPlugin[]
    santaDataFixersVersioningConfig: FixerVersioningConfig
}

export interface MigratorsSettings {
    pageMigrators: AnyPageMigrator[]
    siteMigrators: SiteMigrator[]
    migratorsVersioningConfig: FixerVersioningConfig
}

const createExtension: CreateExtension = ({experimentInstance, logger, dsConfig}) => {
    const createExtensionAPI = ({dal, extensionAPI, pointers, coreConfig}: CreateExtArgs): FixersExtensionAPI => {
        const fixersApi = {
            dal,
            extensionAPI,
            pointers,
            config: coreConfig,
            experimentInstance,
            logger
        }

        const fixPage = async (
            pageId: string,
            santaDataFixersSettings: SantaDataFixersSettings,
            migratorsSettings: MigratorsSettings
        ) => {
            if (!dsConfig.skipFixers) {
                const {santaDataFixerPlugins, santaDataFixersVersioningConfig} = santaDataFixersSettings
                await runSantaDataFixers(
                    fixersApi,
                    pageId,
                    dsConfig,
                    santaDataFixerPlugins,
                    santaDataFixersVersioningConfig
                )
            }

            if (!dsConfig.skipDataMigrators) {
                const {pageMigrators, migratorsVersioningConfig} = migratorsSettings
                runPageMigrators(fixersApi, pageId, pageMigrators, migratorsVersioningConfig)
            }

            if (experimentInstance.isOpen('dm_moveValidationBaseline')) {
                dal.safeCommitTransactionWithErrorReport(
                    {
                        failedValidationAfterEarlyBaseline: true,
                        phase: 'fixers',
                        pageId
                    },
                    `dataFixer-per-page-${pageId}`
                )
            } else {
                dal.commitTransaction(`dataFixer-per-page-${pageId}`, true)
            }
        }

        const runSiteMigrators = runSiteMigratorsFactory(dsConfig, fixersApi)

        const fixAllPagesAndMigrateSite = async (
            pageIds: string[],
            santaDataFixersSettings: SantaDataFixersSettings,
            migratorsSettings: MigratorsSettings
        ) => {
            const {page} = extensionAPI as PageExtensionAPI
            const allSitePages = page.getAllPagesIds(true)

            if (pageIds.length === 0) {
                pageIds = allSitePages
            }

            const loadedPageIds = filterLoadedPages(pageIds, page)
            pushMasterPageToEnd(loadedPageIds) // masterPage should be last

            for (const pageId of loadedPageIds) {
                await (extensionAPI.fixers as FixersAPI).fixPage(pageId, santaDataFixersSettings, migratorsSettings)
            }

            if (loadedPageIds.sort().toString() === allSitePages.sort().toString()) {
                const {siteMigrators, migratorsVersioningConfig} = migratorsSettings
                runSiteMigrators(siteMigrators, migratorsVersioningConfig)
            }
        }

        const getFixersChanges = (): Pointer[] => {
            const {snapshots} = extensionAPI as SnapshotExtApi
            const beforeFixersSnapshot = snapshots.getLastSnapshotByTagName(SNAPSHOTS.BEFORE_FIXERS)
            const afterFixersSnapshot = snapshots.getLastSnapshotByTagName(SNAPSHOTS.AFTER_FIXERS)
            const diffs = afterFixersSnapshot?.diffDeepCompare(beforeFixersSnapshot, PAGE_FRAGMENT_TYPE)
            if (!diffs) {
                return []
            }

            const changes: Pointer[] = []
            for (const [type, dataNodes] of Object.entries(diffs)) {
                for (const [id] of Object.entries(dataNodes)) {
                    changes.push(pointers.getPointer(id, type))
                }
            }

            return changes
        }

        return {
            fixers: {
                fixPage,
                runSiteMigrators,
                fixAllPagesAndMigrateSite,
                getFixersChanges
            }
        }
    }

    return {
        name: 'fixers',
        createExtensionAPI
    }
}

export {createExtension}

export interface FixersAPI extends DeepFunctionMap {
    fixPage(
        pageId: string,
        santaDataFixersSettings: SantaDataFixersSettings,
        migratorsSettings: MigratorsSettings
    ): Promise<void>
    runSiteMigrators(siteMigrators: SiteMigrator[], siteMigratorsVersioningConfig: FixerVersioningConfig): void
    fixAllPagesAndMigrateSite(
        pageIds: string[],
        santaDataFixersSettings: SantaDataFixersSettings,
        migratorsSettings: MigratorsSettings
    ): Promise<void>
    getFixersChanges(): Pointer[]
}

export interface FixersExtensionAPI extends ExtensionAPI {
    fixers: FixersAPI
}
